import { MouseEventHandler, useCallback, useState, useMemo, useRef } from 'react';
import { DateRange } from './DateRangePicker.types';
import useLocale from 'yoda-ui/utils/useLocale';
import { useYodaField, UseYodaFieldProps } from 'yoda-ui/yodaForm/hooks/useYodaField';

const useDateRangePicker = ({ name, ...props }: UseYodaFieldProps) => {
  const { locale } = useLocale();
  const {
    onChangeFieldDateRange,
    fieldValue,
    fieldShowError,
    fieldErrorMessage,
    setFormIsDirty,
  } = useYodaField({ name, ...props });

  const [calendarOpen, setCalendarOpen] = useState(false);
  const isClearingRef = useRef(false);

  const hideCalendar = useCallback(() => setCalendarOpen(false), []);
  const showCalendar = useCallback(() => setCalendarOpen(true), []);

  const handleChange = useCallback((date: DateRange | null) => {
    if (isClearingRef.current) {
      isClearingRef.current = false;
      return;
    }

    onChangeFieldDateRange(date);
    setFormIsDirty(true);
  }, [onChangeFieldDateRange, setFormIsDirty]);

  const handleClearButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.stopPropagation();
    if (fieldValue && fieldValue.length > 0) {
      isClearingRef.current = true;
      onChangeFieldDateRange([null, null]);
    }
  }, [fieldValue, onChangeFieldDateRange]);

  const clearButtonDisabled = useMemo(() => (!fieldValue || fieldValue.length <= 1 || fieldValue[1] == null), [fieldValue]);

  return {
    locale,
    fieldValue,
    fieldShowError,
    fieldErrorMessage,
    calendarOpen,
    hideCalendar,
    showCalendar,
    handleChange,
    handleClearButtonClick,
    clearButtonDisabled,
  };
};

export default useDateRangePicker;
